import React, { createContext, useCallback, useState } from 'react';
import _has from 'lodash/has';
import _isArray from 'lodash/isArray';
import _isNumber from 'lodash/isNumber';

import { useFetch, useMutation } from 'utils/hooks';

/**
 * @type {React.Context<{
 *  notes: Object[],
 *  isError: boolean,
 *  isLoading: boolean,
 *  isDisabled: boolean,
 *  isCreating: boolean,
 *  isCreatable: boolean,
 *  isDeletable: boolean,
 *  deletingId: string,
 *  onCreate: (payload: Record<string, *>, callback: import("utils/types").MutationCallback) => void,
 *  onDelete: (noteId: string, index: number, callback: import("utils/types").MutationCallback) => void
 * }>}
 */
export const NotesContext = createContext();

/**
 * @type {Record<import('utils/types').NoteType, {
 *  GET: string,
 *  POST: string
 * }>}
 * */
export const API_KEYS = {
  REQUEST: {
    GET: 'requests.notes.get',
    POST: 'requests.notes.post',
    DELETE: 'requests.notes.delete'
  },
  RETEX: {
    GET: 'retexes.notes.get',
    POST: 'retexes.notes.post',
    DELETE: 'retexes.notes.delete'
  }
};

/**
 * @param {Object} props
 * @param {import('utils/types').NoteType} props.type - The type of notes to manage
 * @param {string} props.id - The entity ID to associated with the notes
 * @param {boolean} props.isDisabled - If true, entire component will be in disabled state
 * @param {boolean} props.isCreatable - If true, user can create new notes
 * @param {boolean} props.isDeletable - If true, user can delete existing notes
 * @param {React.ReactNode} props.children
 */
export function NotesProvider({
  id = null,
  type = null,
  isDisabled = false,
  isCreatable = true,
  isDeletable = true,
  children = null
}) {
  if (!id) throw new Error('"id" prop is required in NotesProvider');
  if (!type) throw new Error('"type" prop is required in NotesProvider');
  if (!_has(API_KEYS, type)) throw new Error(`Invalid note "type": ${type}`);

  const [notes, setNotes] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const { isError, isLoading } = useFetch(API_KEYS[type].GET, {
    initialData: [],
    loadOnMount: !!id,
    routeParams: { id },
    onTransform: data => (_isArray(data) ? data : []),
    onSuccess: setNotes
  });

  const { mutate: onCreate, isProcessing: isCreating } = useMutation(
    API_KEYS[type].POST,
    'POST',
    {
      routeParams: { id },
      onValidate: () => id && isCreatable && !isDisabled,
      onSuccess: note => setNotes(curr => [note, ...curr])
    }
  );

  const { mutate: deleteNote, isProcessing: isDeleting } = useMutation(
    API_KEYS[type].DELETE,
    'DELETE',
    {
      routeParams: { id },
      onValidate: () => id && isDeletable && !isDisabled
    }
  );

  const onDelete = useCallback(
    (noteId, index, callback = () => {}) => {
      if (!noteId || !_isNumber(index) || deletingId === noteId) return;

      setDeletingId(noteId);
      deleteNote({ routeParams: { id, noteId } }, (err, data) => {
        callback(err, data);
        setDeletingId(null);
        if (!err) {
          setNotes(currNotes => {
            const newNotes = [...currNotes];
            newNotes.splice(index, 1);
            return newNotes;
          });
        }
      });
    },
    [id, deletingId, deleteNote]
  );

  return (
    <NotesContext.Provider
      value={{
        notes,
        isError,
        isLoading,
        isDisabled,
        isCreating,
        isDeleting,
        isCreatable,
        isDeletable,
        deletingId,
        onCreate,
        onDelete
      }}
    >
      {children}
    </NotesContext.Provider>
  );
}
